import { useEffect, useMemo, useRef } from "react";

import { faChartSimple, faThumbtack } from "@fortawesome/pro-regular-svg-icons";
import { useVegaEmbed } from "react-vega";
import type { Loader } from "vega";
import { expressionInterpreter } from "vega-interpreter";
import type { TopLevelSpec } from "vega-lite";

import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { EmptyState } from "src/design-system/EmptyState";
import "src/flow/analytics/m1/VisualizeMetrics/MetricsChart.css";

import type { MetricsQueryRow } from "src/flow/analytics/m1/useMetricsQuery";
import { TAKTILE_CHART_CONFIG } from "src/flow/analytics/m1/VisualizeMetrics/liveAnalyticsDomains";
import {
  applySpecialDomainBindings,
  type LabelDictionaries,
} from "src/flow/analytics/m1/VisualizeMetrics/specialDomainScaling";

// Module-level constant so `<MetricsChart />` callers that omit the prop
// don't allocate a fresh `{}` per render and bust VegaChart's useMemo.
const EMPTY_LABEL_DICTIONARIES: LabelDictionaries = {};

const VEGA_SPEC_DISCRIMINATORS = [
  "mark",
  "layer",
  "facet",
  "repeat",
  "concat",
  "vconcat",
  "hconcat",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isVegaLiteSpec = (chartDefinition: Record<string, unknown>): boolean =>
  VEGA_SPEC_DISCRIMINATORS.some((key) => key in chartDefinition);

const extractTitle = (chartDefinition: Record<string, unknown>): string => {
  const { title } = chartDefinition;
  if (typeof title === "string") return title;
  if (isRecord(title) && typeof title.text === "string") return title.text;
  return "";
};

const noopLoader = (): Loader => {
  const reject = (): Promise<never> =>
    Promise.reject(new Error("Vega external resource loading is disabled"));
  return { load: reject, sanitize: reject, http: reject, file: reject };
};

export const NO_EXTERNAL_RESOURCES_LOADER = noopLoader();

// Shallow-merge each object-valued section of TAKTILE_CHART_CONFIG with the
// matching LLM-provided section so a partial override (`axis: { labelFont }`)
// doesn't wipe out our other section defaults (labelColor, gridColor, …).
export const mergeChartConfig = (
  userConfig: Record<string, unknown>,
): Record<string, unknown> => {
  const subsections = Object.fromEntries(
    Object.entries(TAKTILE_CHART_CONFIG)
      .filter(([, base]) => isRecord(base))
      .map(([key, base]) => [
        key,
        {
          ...(base as Record<string, unknown>),
          ...(isRecord(userConfig[key]) ? userConfig[key] : {}),
        },
      ]),
  );
  return { ...TAKTILE_CHART_CONFIG, ...userConfig, ...subsections };
};

const buildSpec = (
  chartDefinition: Record<string, unknown>,
  rows: MetricsQueryRow[],
  labelDictionaries: LabelDictionaries,
): TopLevelSpec => {
  const withDomains = applySpecialDomainBindings(
    chartDefinition as unknown as TopLevelSpec,
    rows,
    labelDictionaries,
  ) as unknown as Record<string, unknown>;
  return {
    ...withDomains,
    width: "container",
    height: "container",
    autosize: { contains: "padding" },
    title: null,
    config: {
      ...mergeChartConfig(
        isRecord(withDomains.config) ? withDomains.config : {},
      ),
      // See OPTI-7076.
      customFormatTypes: false,
    },
    data: { values: rows },
  } as unknown as TopLevelSpec;
};

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Card variant="inverted" className="flex h-full flex-col">
    <Card.Header>
      <Card.Title title={title} />
      <Button iconLeft={faThumbtack} size="sm" variant="primary" disabled>
        Pin
      </Button>
    </Card.Header>
    <Card.Content className="min-h-0 flex-1">{children}</Card.Content>
  </Card>
);

const VegaChart: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
  labelDictionaries: LabelDictionaries;
}> = ({ rows, chartDefinition, labelDictionaries }) => {
  const embedRef = useRef<HTMLDivElement>(null);

  const spec = useMemo(
    () => buildSpec(chartDefinition, rows, labelDictionaries),
    [chartDefinition, rows, labelDictionaries],
  );

  const embed = useVegaEmbed({
    ref: embedRef,
    options: {
      actions: false,
      mode: "vega-lite",
      renderer: "canvas",
      // Security-relevant: AST interpreter avoids Vega's default
      // Function()-based evaluator, which Vega recommends for untrusted specs.
      ast: true,
      expr: expressionInterpreter,
      loader: NO_EXTERNAL_RESOURCES_LOADER,
    },
    spec,
  });

  useEffect(() => {
    const container = embedRef.current;
    if (!container || !embed) return;
    const observer = new ResizeObserver(() => {
      window.dispatchEvent(new Event("resize"));
      embed.view.runAsync();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [embed]);

  return <div ref={embedRef} className="h-full w-full" />;
};

export const MetricsChart: React.FC<{
  rows: MetricsQueryRow[];
  chartDefinition: Record<string, unknown>;
  labelDictionaries?: LabelDictionaries;
}> = ({
  rows,
  chartDefinition,
  labelDictionaries = EMPTY_LABEL_DICTIONARIES,
}) => {
  if (!isVegaLiteSpec(chartDefinition)) {
    return (
      <Card variant="inverted" className="flex h-full flex-col">
        <EmptyState
          description="The chart definition is missing a mark or layer."
          headline="Invalid chart definition"
          icon={faChartSimple}
          size="compact"
          variant="error"
        />
      </Card>
    );
  }

  return (
    <ChartCard title={extractTitle(chartDefinition)}>
      <VegaChart
        chartDefinition={chartDefinition}
        labelDictionaries={labelDictionaries}
        rows={rows}
      />
    </ChartCard>
  );
};
